import { format } from 'date-fns';
import { Download, History } from 'lucide-react';
import { useFileVersions } from '@/api/files';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatBytes } from '@/lib/formatBytes';

interface Props {
  fileId: string;
  /** The current_version_id from the parent File — used to mark which
   *  version row is the active one. */
  currentVersionId?: string | null;
}

export function VersionHistory({ fileId, currentVersionId }: Props) {
  const { data, isLoading, isError, error } = useFileVersions(fileId);

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-sm text-red-600">
        {error instanceof Error ? error.message : 'Failed to load versions.'}
      </div>
    );
  }

  const versions = (data ?? []).slice().sort((a, b) => b.version_number - a.version_number);

  if (versions.length === 0) {
    return (
      <EmptyState
        title="No versions yet"
        description="All future replacements will be tracked here as new versions."
        icon={<History size={24} />}
      />
    );
  }

  return (
    <ul className="divide-y divide-slate-100">
      {versions.map((v) => {
        const isCurrent = currentVersionId === v.id;
        return (
          <li
            key={v.id}
            className={`py-3 flex items-start gap-3${isCurrent ? ' bg-bb-navy/5 -mx-5 px-5' : ''}`}
          >
            <div className="w-10 h-10 bg-bb-navy/10 text-bb-navy flex items-center justify-center font-bold text-sm shrink-0">
              v{v.version_number}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-800">
                  Version {v.version_number}
                </span>
                {isCurrent && <Badge tone="green">Current</Badge>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {format(new Date(v.created_at), 'PP p')} · {formatBytes(v.size_bytes)}
                {v.mime_type ? ` · ${v.mime_type}` : ''}
              </div>
              {v.checksum_sha256 && (
                <div className="text-[10px] text-slate-400 font-mono mt-0.5 truncate" title={v.checksum_sha256}>
                  SHA-256: {v.checksum_sha256.slice(0, 16)}…
                </div>
              )}
            </div>
            <a
              href={`/api/files/${fileId}/versions/${v.id}/download`}
              className="shrink-0 p-1.5 text-slate-400 hover:text-bb-navy"
              title={`Download version ${v.version_number}`}
              download
            >
              <Download size={14} />
            </a>
          </li>
        );
      })}
    </ul>
  );
}
